import { Flex, Input } from '@/components'
import { useMemo } from 'react'
import {
  ColumnWrapper,
  SectionTitle,
  Subtitle,
  TableContainer,
  Title,
  StickyTitleContainer,
  ScrollableContent,
} from '../../Cena.styles'
import { useCenaContext } from '../../Cena.context'
import { CenaTable, CenaTableColumn, GroupedCenaTableData } from '../CenaTable/CenaTable.component'
import { ImprensaInputContainer } from './Imprensa.styles'

type ProcessedGroupedTableData = {
  id: string
  label: string
  value: string
  groupDisplay: string
  rowSpan: number
}

export function CenaImprensa() {
  const { imprensa, updateImprensa } = useCenaContext()

  const handleFieldChange = (field: string, value: string) => {
    updateImprensa({ [field]: value })
  }

  const handlePressApproachChange = (id: string, newValue: string) => {
    updateImprensa({
      pressApproachData: imprensa.pressApproachData.map((row) => (row.id === id ? { ...row, value: newValue } : row)),
    })
  }

  const handlePressVehicleChange = (id: string, newValue: string) => {
    updateImprensa({
      pressVehicleData: imprensa.pressVehicleData.map((row) => (row.id === id ? { ...row, value: newValue } : row)),
    })
  }

  const handleMediaTypeChange = (id: string, label: string, newValue: string) => {
    updateImprensa({
      mediaTypeData: imprensa.mediaTypeData.map((row) =>
        row.id === id && row.label === label ? { ...row, value: newValue } : row,
      ),
    })
  }

  const processedMediaTypeData = useMemo<ProcessedGroupedTableData[]>(() => {
    return imprensa.mediaTypeData.map((item, index, arr) => {
      const isFirstOfGroup = index === 0 || arr[index - 1].id !== item.id
      if (!isFirstOfGroup) {
        return { ...item, groupDisplay: '', rowSpan: 0 }
      }
      const rowSpan = arr.filter((i) => i.id === item.id).length
      return {
        ...item,
        groupDisplay: item.id,
        rowSpan,
      }
    })
  }, [imprensa.mediaTypeData])

  const pressApproachColumns: CenaTableColumn[] = [
    {
      key: 'label',
      title: 'Abordagem',
      align: 'left',
    },
    {
      key: 'value',
      title: 'Pontos',
      align: 'center',
      width: 120,
    },
  ]

  const pressVehicleColumns: CenaTableColumn[] = [
    {
      key: 'label',
      title: 'Peso do Veículo',
      align: 'left',
    },
    {
      key: 'value',
      title: 'Nota Base',
      align: 'center',
      width: 120,
    },
  ]

  const mediaTypeColumns: CenaTableColumn[] = [
    {
      key: 'groupDisplay',
      title: 'Tipo de Mídia',
      align: 'left',
      width: 150,
      isGroupColumn: true,
    },
    {
      key: 'label',
      title: 'Formato',
      align: 'left',
    },
    {
      key: 'value',
      title: 'Ponto',
      align: 'center',
      width: 120,
    },
  ]

  return (
    <ColumnWrapper>
      <StickyTitleContainer>
        <Title>IMPRENSA</Title>
      </StickyTitleContainer>

      <ScrollableContent>
        <Flex $type="column" $align="stretch" $gap={24} style={{ paddingTop: '24px' }}>
          <Subtitle>Variáveis principais de exposição</Subtitle>
          <ImprensaInputContainer>
            <Input
              label="Peso Formato"
              type="number"
              placeholder="Digite o peso"
              value={imprensa.formatWeight}
              onChange={(value) => handleFieldChange('formatWeight', value)}
            />
          </ImprensaInputContainer>
          <TableContainer>
            <CenaTable
              columns={mediaTypeColumns}
              data={processedMediaTypeData as GroupedCenaTableData[]}
              onGroupedCellChange={handleMediaTypeChange}
            />
          </TableContainer>

          <SectionTitle>Peso Abordagem</SectionTitle>
          <ImprensaInputContainer>
            <Input
              label="Multiplicador"
              type="number"
              placeholder="Multiplicador"
              value={imprensa.approachMultiplier}
              onChange={(value) => handleFieldChange('approachMultiplier', value)}
            />
          </ImprensaInputContainer>
          <TableContainer>
            <CenaTable
              columns={pressApproachColumns}
              data={imprensa.pressApproachData}
              onCellChange={(id, _, value) => handlePressApproachChange(id, value)}
            />
          </TableContainer>

          <SectionTitle>Peso do Veículo</SectionTitle>
          <ImprensaInputContainer>
            <Input
              label="Multiplicador"
              type="number"
              placeholder="Multiplicador"
              value={imprensa.vehicleMultiplier}
              onChange={(value) => handleFieldChange('vehicleMultiplier', value)}
            />
          </ImprensaInputContainer>
          <TableContainer>
            <CenaTable
              columns={pressVehicleColumns}
              data={imprensa.pressVehicleData}
              onCellChange={(id, _, value) => handlePressVehicleChange(id, value)}
            />
          </TableContainer>

          <Subtitle>Variáveis complementares de exposição</Subtitle>
          <ImprensaInputContainer>
            <Input
              label="Multiplicador"
              type="number"
              placeholder="Multiplicador"
              value={imprensa.complementaryMultiplier}
              onChange={(value) => handleFieldChange('complementaryMultiplier', value)}
            />
          </ImprensaInputContainer>
          <ImprensaInputContainer>
            <Input
              label="Mensagem-chave"
              type="number"
              placeholder="Pontos"
              value={imprensa.keyMessage}
              onChange={(value) => handleFieldChange('keyMessage', value)}
            />
          </ImprensaInputContainer>
          <ImprensaInputContainer>
            <Input
              label="Porta-voz mencionado"
              type="number"
              placeholder="Pontos"
              value={imprensa.spokespersonMentioned}
              onChange={(value) => handleFieldChange('spokespersonMentioned', value)}
            />
          </ImprensaInputContainer>
          <ImprensaInputContainer>
            <Input
              label="Menção no título"
              type="number"
              placeholder="Pontos"
              value={imprensa.titleMention}
              onChange={(value) => handleFieldChange('titleMention', value)}
            />
          </ImprensaInputContainer>
        </Flex>
      </ScrollableContent>
    </ColumnWrapper>
  )
}
